// Página SupplyForm - Formulario para crear/editar insumos

import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useSupply, useSupplyOperations } from '../../hooks/useSupplies'
import { getAllSuppliers } from '../../services/suppliers.service'
import MultiSelect from '../../components/MultiSelect'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './SupplyForm.css'

function SupplyForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id

  const { supply, loading: loadingSupply } = useSupply(id)
  const { create, update, loading: saving } = useSupplyOperations()
  const [suppliers, setSuppliers] = useState([])
  const [notification, setNotification] = useState(null)

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    code: '',
    category: 'herramientas',
    unit: 'unidad',
    supplierIds: [],
    trackStock: false,
    currentStock: 0,
    minStock: null,
    description: '',
    notes: '',
    active: true
  })

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const suppliersData = await getAllSuppliers()
        setSuppliers(suppliersData)
      } catch (error) {
        console.error('Error cargando proveedores:', error)
      }
    }
    loadSuppliers()
  }, [])

  useEffect(() => {
    if (isEditing && supply) {
      setFormData({
        name: supply.name || '',
        sku: supply.sku || '',
        code: supply.code || '',
        category: supply.category || 'herramientas',
        unit: supply.unit || 'unidad',
        supplierIds: supply.supplierIds || [],
        trackStock: supply.trackStock || false,
        currentStock: supply.currentStock || 0,
        minStock: supply.minStock || null,
        description: supply.description || '',
        notes: supply.notes || '',
        active: supply.active !== false
      })
    }
  }, [isEditing, supply])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving) return

    // Validación
    if (!formData.name.trim() || !formData.sku.trim()) {
      setNotification({
        type: 'error',
        message: 'Nombre y SKU son obligatorios'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    try {
      if (isEditing) {
        await update(id, formData)
        setNotification({
          type: 'success',
          message: 'Insumo actualizado correctamente'
        })
      } else {
        await create(formData)
        setNotification({
          type: 'success',
          message: 'Insumo creado correctamente'
        })
      }
      setTimeout(() => {
        navigate('/admin/insumos')
      }, 1500)
    } catch (error) {
      console.error('Error guardando insumo:', error)
      setNotification({
        type: 'error',
        message: `Error al guardar: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  if (loadingSupply) {
    return (
      <div className="supply-form-page">
        <div className="loading-state">
          <p>Cargando insumo...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="supply-form-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="form-header">
        <div>
          <Link to="/admin/insumos">
            <button className="back-button">← Volver</button>
          </Link>
          <h1>{isEditing ? 'Editar Insumo' : 'Nuevo Insumo'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="supply-form">
        <div className="form-section">
          <h3>Información Básica</h3>
          
          <div className="form-group">
            <label htmlFor="name">
              Nombre <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sku">
                SKU <span className="required">*</span>
              </label>
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                required
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="code">Código Interno</label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                disabled={saving}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">
                Categoría <span className="required">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                disabled={saving}
                className="form-input"
              >
                <option value="herramientas">Herramientas</option>
                <option value="materiales">Materiales</option>
                <option value="quimicos">Químicos</option>
                <option value="otros">Otros</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="unit">
                Unidad de Medida <span className="required">*</span>
              </label>
              <select
                id="unit"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                required
                disabled={saving}
                className="form-input"
              >
                <option value="unidad">Unidad</option>
                <option value="kg">Kilogramo (kg)</option>
                <option value="litro">Litro</option>
                <option value="metro">Metro</option>
                <option value="caja">Caja</option>
                <option value="paquete">Paquete</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Proveedores</h3>
          
          <div className="form-group">
            <label htmlFor="supplierIds">Proveedores (opcional)</label>
            {suppliers.length === 0 ? (
              <div className="no-suppliers-message">
                <p>No hay proveedores disponibles.</p>
                <Link to="/admin/proveedores/nuevo">Crear proveedor</Link>
              </div>
            ) : (
              <MultiSelect
                options={suppliers.map(supplier => ({
                  id: supplier.email,
                  label: supplier.name,
                  sublabel: supplier.businessName || undefined
                }))}
                selected={formData.supplierIds}
                onChange={(selectedIds) => {
                  setFormData(prev => ({
                    ...prev,
                    supplierIds: selectedIds
                  }))
                }}
                placeholder="Seleccionar proveedores..."
                searchPlaceholder="Buscar proveedores..."
                disabled={saving}
              />
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Stock (Opcional)</h3>
          
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="trackStock"
                checked={formData.trackStock}
                onChange={handleChange}
                disabled={saving}
              />
              <span>Rastrear stock de este insumo</span>
            </label>
          </div>

          {formData.trackStock && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="currentStock">Stock Actual</label>
                  <input
                    type="number"
                    id="currentStock"
                    name="currentStock"
                    value={formData.currentStock}
                    onChange={handleChange}
                    min="0"
                    disabled={saving}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="minStock">Stock Mínimo (opcional)</label>
                  <input
                    type="number"
                    id="minStock"
                    name="minStock"
                    value={formData.minStock || ''}
                    onChange={handleChange}
                    min="0"
                    disabled={saving}
                    className="form-input"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="form-section">
          <h3>Información Adicional</h3>
          
          <div className="form-group">
            <label htmlFor="description">Descripción</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notas Internas</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              disabled={saving}
              className="form-input"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                disabled={saving}
              />
              <span>Insumo activo</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/insumos')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : (isEditing ? 'Actualizar Insumo' : 'Crear Insumo')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default SupplyForm

